import type { ReactNode } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { Pressable } from '../../Common/Pressable'
import type { WorkspaceBudgetSummary } from '../../../types/budget'
import type { StockItem } from '../../../types/stock'
import { isStockItemLow } from '../../../utils/stockLevels'
import type { TeachingOverviewMetrics } from '../../../services/eduTimetableService'

export interface ModulesSectionProps {
  stockEnabled?: boolean
  stockItems?: StockItem[]
  budgetEnabled?: boolean
  budgetSummary?: WorkspaceBudgetSummary | null
  teachingEnabled?: boolean
  teachingMetrics?: TeachingOverviewMetrics | null
  onOpenModule?: (moduleKey: string) => void
  toolsNode?: ReactNode
}

export const ModulesSection = ({
  stockEnabled = false,
  stockItems = [],
  budgetEnabled = false,
  budgetSummary = null,
  teachingEnabled = false,
  teachingMetrics = null,
  onOpenModule,
  toolsNode,
}: ModulesSectionProps) => {
  const { t } = useNls()

  const hasStock = stockEnabled
  const hasBudget = budgetEnabled && Boolean(budgetSummary)
  const hasTeaching = teachingEnabled

  if (!hasStock && !hasBudget && !hasTeaching) {
    return null
  }

  const lowStockCount = stockItems.filter(isStockItemLow).length
  const totalStockCount = stockItems.length

  const budgetSpent = budgetSummary?.totalActualSpend ?? 0
  const budgetLimit = budgetSummary?.totalBudgetLimit ?? 0
  const budgetPercent =
    budgetLimit > 0 ? Math.min(100, Math.round((budgetSpent / budgetLimit) * 100)) : 0

  return (
    <div style={{ position: 'relative' }} data-testid="overview-modules-section">
      {toolsNode}
      <div className="overviewModulesGrid">
        {hasStock && (
          <div className="overviewModuleCard" data-module-card="stock">
            <div className="overviewModuleCard__top">
              <span className="overviewModuleCard__title">{t('NLSOverviewStockTitle')}</span>
              {onOpenModule && (
                <Pressable
                  type="button"
                  className="overviewCard__link"
                  onClick={() => onOpenModule('stock')}
                >
                  {t('NLSOverviewModuleOpenLink')}
                </Pressable>
              )}
            </div>
            <div className="overviewModuleCard__metric">{lowStockCount}</div>
            <div className="overviewModuleCard__sub">
              {t('NLSOverviewStockReorderPill', { count: lowStockCount })} ·{' '}
              {t('NLSOverviewStockTotalPill', { count: totalStockCount })}
            </div>
          </div>
        )}

        {hasBudget && (
          <div className="overviewModuleCard" data-module-card="budget">
            <div className="overviewModuleCard__top">
              <span className="overviewModuleCard__title">{t('NLSOverviewBudgetTitle')}</span>
              {onOpenModule && (
                <Pressable
                  type="button"
                  className="overviewCard__link"
                  onClick={() => onOpenModule('budget')}
                >
                  {t('NLSOverviewModuleOpenLink')}
                </Pressable>
              )}
            </div>
            <div className="overviewModuleCard__metric">
              {budgetSpent.toLocaleString()} / {budgetLimit.toLocaleString()}
            </div>
            <div className="overviewProgressBar" style={{ margin: '4px 0' }}>
              <div
                className="overviewProgressBar__fill"
                style={{
                  width: `${budgetPercent}%`,
                  background: 'var(--info)',
                }}
              />
            </div>
            <div className="overviewModuleCard__sub">
              {t('NLSOverviewBudgetUsedPill', { percent: budgetPercent })}
            </div>
          </div>
        )}

        {hasTeaching && (
          <div className="overviewModuleCard" data-module-card="teaching">
            <div className="overviewModuleCard__top">
              <span className="overviewModuleCard__title">
                {t('NLSOverviewTeachingTitle')}
              </span>
              {onOpenModule && (
                <Pressable
                  type="button"
                  className="overviewCard__link"
                  onClick={() => onOpenModule('teaching')}
                >
                  {t('NLSOverviewModuleOpenLink')}
                </Pressable>
              )}
            </div>
            <div className="overviewTeachingMetrics">
              {teachingMetrics?.attendanceToday != null && (
                <div className="overviewTeachingMetricRow">
                  <span>{t('NLSOverviewTeachingAttendanceToday')}</span>
                  <strong>{teachingMetrics.attendanceToday}</strong>
                </div>
              )}
              {teachingMetrics?.classesToday != null && (
                <div className="overviewTeachingMetricRow">
                  <span>{t('NLSOverviewTeachingClassesToday')}</span>
                  <strong>{teachingMetrics.classesToday}</strong>
                </div>
              )}
              {teachingMetrics?.assessmentsDue != null && (
                <div className="overviewTeachingMetricRow">
                  <span>{t('NLSOverviewTeachingAssessmentsDue')}</span>
                  <strong>{teachingMetrics.assessmentsDue}</strong>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
